import { supabase } from '../lib/supabase';
import { sanitizeForSave, sanitizeForRender } from '@/utils/sanitizeHtml';

export interface Entry {
  id: string;
  title: string;
  slug: string;
  pillar: 'thought' | 'image' | null; // New schema uses pillar instead of entry_type
  entry_type?: 'thought' | 'image' | 'project'; // Keep for backward compatibility
  thought_mode?: 'question' | 'observation' | 'note' | 'article' | 'essay' | null;
  image_mode?: 'photo' | 'set' | 'photoessay' | 'video' | null;
  status: 'draft' | 'published';
  content?: string; // Keep for backward compatibility
  body_html?: string;
  excerpt?: string;
  series?: string | null;
  featured: boolean;
  publish_date?: string | null; // Date only
  coverImageUrl: string | null;
  projectId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  viewCount: number;
  metadata: Record<string, unknown>;
  show_watermark?: boolean; // Show copyright watermark on images
  focal_x?: number;
  focal_y?: number;
  width?: number;
  height?: number;
  keep_x?: number | null;
  keep_y?: number | null;
  keep_w?: number | null;
  keep_h?: number | null;
  keep_updated_at?: string | null;
  crops?: Record<string, CropRect>;
}

interface EntryRow {
  id: string;
  title: string;
  slug: string;
  entry_type?: string | null; // Old field, kept for compatibility
  pillar?: string | null;
  thought_mode?: string | null;
  image_mode?: string | null;
  content: string | null;
  body_html: string | null;
  excerpt: string | null;
  series?: string | null;
  featured?: boolean;
  publish_date?: string | null;
  cover_image_url: string | null;
  status: string;
  project_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  view_count: number;
  metadata: Record<string, unknown>;
  show_watermark?: boolean; // Show copyright watermark on images
  focal_x?: number;
  focal_y?: number;
  width?: number;
  height?: number;
  keep_x?: number | null;
  keep_y?: number | null;
  keep_w?: number | null;
  keep_h?: number | null;
  keep_updated_at?: string | null;
}

export type CropRect = {
  crop_x: number;
  crop_y: number;
  crop_w: number;
  crop_h: number;
};

const convertToEntry = (row: EntryRow): Entry => ({
  id: row.id,
  title: row.title,
  slug: row.slug,
  pillar: (row.pillar as 'thought' | 'image' | null) || (row.entry_type === 'thought' ? 'thought' : row.entry_type === 'image' ? 'image' : null),
  entry_type: row.entry_type as 'thought' | 'image' | 'project' | undefined, // Keep for backward compatibility
  thought_mode: row.thought_mode as 'question' | 'observation' | 'note' | 'article' | 'essay' | null | undefined,
  image_mode: row.image_mode as 'photo' | 'set' | 'photoessay' | 'video' | null | undefined,
  content: row.content ?? undefined,
  body_html: row.body_html ?? undefined,
  excerpt: row.excerpt ?? undefined,
  series: row.series,
  // Prefer column value, fallback to metadata if older rows stored it there
  featured: row.featured ?? (row.metadata?.featured as boolean | undefined) ?? false,
  publish_date: row.publish_date,
  coverImageUrl: row.cover_image_url,
  status: row.status as 'draft' | 'published',
  projectId: row.project_id,
  createdBy: row.created_by,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  publishedAt: row.published_at,
  viewCount: row.view_count,
  metadata: row.metadata,
  show_watermark: row.show_watermark ?? true, // Default to showing watermark
  focal_x: row.focal_x,
  focal_y: row.focal_y,
  width: row.width,
  height: row.height,
  keep_x: row.keep_x ?? null,
  keep_y: row.keep_y ?? null,
  keep_w: row.keep_w ?? null,
  keep_h: row.keep_h ?? null,
  keep_updated_at: row.keep_updated_at ?? null,
});

async function hydrateCrops(entries: Entry[]): Promise<Entry[]> {
  const ids = entries.map((e) => e.id);
  if (ids.length === 0) return entries;

  const { data: cropRows } = await supabase
    .from('image_crops')
    .select('image_id, ratio_key, crop_x, crop_y, crop_w, crop_h')
    .in('image_id', ids);

  const cropMap = new Map<string, Record<string, CropRect>>();
  (cropRows || []).forEach((row) => {
    const existing = cropMap.get(row.image_id) || {};
    existing[row.ratio_key] = {
      crop_x: row.crop_x,
      crop_y: row.crop_y,
      crop_w: row.crop_w,
      crop_h: row.crop_h,
    };
    cropMap.set(row.image_id, existing);
  });

  return entries.map((e) => ({
    ...e,
    crops: cropMap.get(e.id) || e.crops,
  }));
}

export const entryService = {
  async getAllPublished(pillar?: 'thought' | 'image'): Promise<{ data: Entry[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from('entries')
        .select('*')
        .eq('status', 'published');

      // Support both new (pillar) and old (entry_type) schema
      if (pillar) {
        query = query.or(`pillar.eq.${pillar},entry_type.eq.${pillar}`);
      }

      const { data, error } = await query.order('published_at', { ascending: false });

      if (error) throw error;

      let entries = data?.map(convertToEntry) ?? [];
      if (entries.length > 0) {
        entries = await hydrateCrops(entries);
      }

      return { 
        data: entries, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async getBySlug(slug: string): Promise<{ data: Entry | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('entries')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (error) throw error;

      let entry = data ? convertToEntry(data) : null;

      if (entry) {
        // Increment view count (client-gated to reduce abuse)
        if (typeof window !== 'undefined') {
          const storageKey = `entry-viewed-${entry.id}`;
          const lastViewed = localStorage.getItem(storageKey);
          if (!lastViewed) {
            await supabase
              .from('entries')
              .update({ view_count: entry.viewCount + 1 })
              .eq('id', entry.id);
            localStorage.setItem(storageKey, new Date().toISOString());
          }
        }

        const hydrated = await hydrateCrops([entry]);
        entry = hydrated[0];
      }

      return { 
        data: entry, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async getByProject(projectId: string): Promise<{ data: Entry[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('entries')
        .select('*')
        .eq('project_id', projectId)
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (error) throw error;

      return { 
        data: data?.map(convertToEntry) ?? [], 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async create(entry: Omit<Entry, 'id' | 'createdAt' | 'updatedAt' | 'viewCount' | 'createdBy'>): Promise<{ data: Entry | null; error: Error | null }> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      // Map pillar to entry_type for backward compatibility (entry_type is NOT NULL)
      const entryType = entry.pillar === 'thought' ? 'thought' : entry.pillar === 'image' ? 'image' : entry.entry_type || 'thought';

      const insertData: Record<string, unknown> = {
        title: entry.title,
        slug: entry.slug,
        entry_type: entryType, // Required: entry_type is NOT NULL in database
        pillar: entry.pillar || entryType, // Set pillar from entry_type if not provided
        thought_mode: entry.thought_mode || null,
        image_mode: entry.image_mode || null,
        body_html: entry.body_html ? sanitizeForSave(entry.body_html) : null,
        series: entry.series || null,
        featured: entry.featured ?? false,
        publish_date: entry.publish_date || null,
        status: entry.status,
        project_id: entry.projectId || null,
        created_by: userData.user.id,
        published_at: entry.publishedAt || null,
        metadata: entry.metadata || {},
        show_watermark: entry.show_watermark ?? true, // Default to showing watermark
      };

      // Keep old fields for backward compatibility
      if (entry.content) {
        insertData.content = sanitizeForSave(entry.content);
      }
      if (entry.excerpt) {
        insertData.excerpt = sanitizeForSave(entry.excerpt);
      }
      if (entry.coverImageUrl) {
        insertData.cover_image_url = entry.coverImageUrl;
      }

      const { data, error } = await supabase
        .from('entries')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw new Error(`Failed to create entry: ${error.message} (Code: ${error.code})`);
      }

      try {
        if (entry.metadata?.subjectIds && Array.isArray(entry.metadata.subjectIds)) {
          const subSubjectIds = entry.metadata?.subSubjectIds as string[] | undefined;
          const subjectInserts = entry.metadata.subjectIds.map((subjectId: string, index: number) => ({
            entry_id: data.id,
            subject_id: subjectId,
            sub_subject_id: subSubjectIds?.[index] || null,
            is_primary: index === 0,
          }));

          await supabase.from('entry_subjects').insert(subjectInserts);
        }

        if (entry.metadata?.tones && Array.isArray(entry.metadata.tones)) {
          const toneInserts = entry.metadata.tones.map((tone: string) => ({
            entry_id: data.id,
            tone: tone,
          }));

          await supabase.from('entry_tones').insert(toneInserts);
        }

        if (entry.metadata?.intents && Array.isArray(entry.metadata.intents)) {
          const intentInserts = entry.metadata.intents.map((intent: string) => ({
            entry_id: data.id,
            intent: intent,
          }));

          await supabase.from('entry_intents').insert(intentInserts);
        }
      } catch (taxonomyError) {
        // Attempt rollback to avoid partial writes
        await supabase.from('entries').delete().eq('id', data.id);
        throw taxonomyError;
      }

      return { 
        data: data ? convertToEntry(data) : null, 
        error: null 
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('EntryService.create error:', errorMessage, error);
      return { 
        data: null, 
        error: new Error(errorMessage)
      };
    }
  },

  async getAll(): Promise<{ data: Entry[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { 
        data: data?.map(convertToEntry) ?? [], 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async getById(id: string): Promise<{ data: Entry | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('entries')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return { data: null, error: null };
        throw error;
      }

      const entry = data ? convertToEntry(data) : null;

      // Fetch related data (subjects, tones, intents)
      if (entry) {
        // Fetch subjects
        const { data: subjectsData } = await supabase
          .from('entry_subjects')
          .select('subject_id, sub_subject_id')
          .eq('entry_id', id);

        if (subjectsData) {
          entry.metadata = {
            ...entry.metadata,
            subjectIds: subjectsData.map(s => s.subject_id),
            subSubjectIds: subjectsData.map(s => s.sub_subject_id).filter(Boolean),
          };
        }

        // Fetch tones
        const { data: tonesData } = await supabase
          .from('entry_tones')
          .select('tone')
          .eq('entry_id', id);

        if (tonesData) {
          entry.metadata = {
            ...entry.metadata,
            tones: tonesData.map(t => t.tone),
          };
        }

        // Fetch intents
        const { data: intentsData } = await supabase
          .from('entry_intents')
          .select('intent')
          .eq('entry_id', id);

        if (intentsData) {
          entry.metadata = {
            ...entry.metadata,
            intents: intentsData.map(i => i.intent),
          };
        }
      }

      return { 
        data: entry, 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching entry by ID:', error);
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async update(id: string, entry: Partial<Omit<Entry, 'id' | 'createdAt' | 'updatedAt' | 'viewCount'>>): Promise<{ data: Entry | null; error: Error | null }> {
    try {
      const updateData: Record<string, unknown> = {};
      const needsTaxonomyUpdate =
        entry.metadata?.subjectIds !== undefined ||
        entry.metadata?.tones !== undefined ||
        entry.metadata?.intents !== undefined;
      let previousTaxonomy:
        | {
            subjects: { entry_id: string; subject_id: string | null; sub_subject_id: string | null; is_primary?: boolean | null }[];
            tones: { entry_id: string; tone: string | null }[];
            intents: { entry_id: string; intent: string | null }[];
          }
        | null = null;

      if (needsTaxonomyUpdate) {
        const [subjectsPrev, tonesPrev, intentsPrev] = await Promise.all([
          supabase.from('entry_subjects').select('entry_id, subject_id, sub_subject_id, is_primary').eq('entry_id', id),
          supabase.from('entry_tones').select('entry_id, tone').eq('entry_id', id),
          supabase.from('entry_intents').select('entry_id, intent').eq('entry_id', id),
        ]);

        previousTaxonomy = {
          subjects: subjectsPrev.data ?? [],
          tones: tonesPrev.data ?? [],
          intents: intentsPrev.data ?? [],
        };
      }
      
      if (entry.title !== undefined) updateData.title = entry.title;
      if (entry.slug !== undefined) updateData.slug = entry.slug;
      if (entry.pillar !== undefined) updateData.pillar = entry.pillar;
      if (entry.thought_mode !== undefined) updateData.thought_mode = entry.thought_mode;
      if (entry.image_mode !== undefined) updateData.image_mode = entry.image_mode;
      if (entry.body_html !== undefined) updateData.body_html = entry.body_html ? sanitizeForSave(entry.body_html) : null;
      if (entry.series !== undefined) updateData.series = entry.series;
      if (entry.featured !== undefined) updateData.featured = entry.featured;
      if (entry.publish_date !== undefined) updateData.publish_date = entry.publish_date;
      if (entry.status !== undefined) updateData.status = entry.status;
      if (entry.projectId !== undefined) updateData.project_id = entry.projectId;
      if (entry.publishedAt !== undefined) updateData.published_at = entry.publishedAt;
      if (entry.metadata !== undefined) updateData.metadata = entry.metadata;
      if (entry.show_watermark !== undefined) updateData.show_watermark = entry.show_watermark;

      // Keep old fields for backward compatibility
      if (entry.entry_type !== undefined) updateData.entry_type = entry.entry_type;
      if (entry.content !== undefined) updateData.content = entry.content ? sanitizeForSave(entry.content) : null;
      if (entry.excerpt !== undefined) updateData.excerpt = entry.excerpt ? sanitizeForSave(entry.excerpt) : null;
      if (entry.coverImageUrl !== undefined) updateData.cover_image_url = entry.coverImageUrl;

      const { data, error } = await supabase
        .from('entries')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      try {
        // Update subjects if provided
        if (entry.metadata?.subjectIds !== undefined) {
          await supabase.from('entry_subjects').delete().eq('entry_id', id);
          
          if (Array.isArray(entry.metadata.subjectIds) && entry.metadata.subjectIds.length > 0) {
            const subSubjectIds = entry.metadata?.subSubjectIds as string[] | undefined;
            const subjectInserts = entry.metadata.subjectIds.map((subjectId: string, index: number) => ({
              entry_id: id,
              subject_id: subjectId,
              sub_subject_id: subSubjectIds?.[index] || null,
              is_primary: index === 0,
            }));

            await supabase.from('entry_subjects').insert(subjectInserts);
          }
        }

        // Update tones if provided
        if (entry.metadata?.tones !== undefined) {
          await supabase.from('entry_tones').delete().eq('entry_id', id);
          
          if (Array.isArray(entry.metadata.tones) && entry.metadata.tones.length > 0) {
            const toneInserts = entry.metadata.tones.map((tone: string) => ({
              entry_id: id,
              tone: tone,
            }));

            await supabase.from('entry_tones').insert(toneInserts);
          }
        }

        // Update intents if provided
        if (entry.metadata?.intents !== undefined) {
          await supabase.from('entry_intents').delete().eq('entry_id', id);
          
          if (Array.isArray(entry.metadata.intents) && entry.metadata.intents.length > 0) {
            const intentInserts = entry.metadata.intents.map((intent: string) => ({
              entry_id: id,
              intent: intent,
            }));

            await supabase.from('entry_intents').insert(intentInserts);
          }
        }
      } catch (taxonomyError) {
        // Attempt to restore previous taxonomy to avoid partial state
        if (previousTaxonomy) {
          await supabase.from('entry_subjects').delete().eq('entry_id', id);
          await supabase.from('entry_tones').delete().eq('entry_id', id);
          await supabase.from('entry_intents').delete().eq('entry_id', id);

          if (previousTaxonomy.subjects.length > 0) {
            await supabase.from('entry_subjects').insert(previousTaxonomy.subjects);
          }
          if (previousTaxonomy.tones.length > 0) {
            await supabase.from('entry_tones').insert(previousTaxonomy.tones);
          }
          if (previousTaxonomy.intents.length > 0) {
            await supabase.from('entry_intents').insert(previousTaxonomy.intents);
          }
        }
        throw taxonomyError;
      }

      return { 
        data: data ? convertToEntry(data) : null, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async delete(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('entries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },
};

/**
 * Create a new entry with sanitized body_html
 */
export async function createEntry(entryData: Partial<Entry>): Promise<Entry> {
  try {
    // Sanitize body_html before saving
    const sanitizedData = {
      ...entryData,
      body_html: entryData.body_html ? sanitizeForSave(entryData.body_html) : null,
    };

    const { data, error } = await supabase
      .from('entries')
      .insert([sanitizedData])
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create entry: ${error.message}`);
    }

    // Sanitize body_html again on render
    if (data.body_html) {
      data.body_html = sanitizeForRender(data.body_html);
    }

    return data;
  } catch (error) {
    console.error('Error creating entry:', error);
    throw error;
  }
}

/**
 * Update an existing entry with sanitized body_html
 */
export async function updateEntry(id: string, entryData: Partial<Entry>): Promise<Entry> {
  try {
    // Sanitize body_html before saving
    const sanitizedData = {
      ...entryData,
      body_html: entryData.body_html ? sanitizeForSave(entryData.body_html) : undefined,
    };

    const { data, error } = await supabase
      .from('entries')
      .update(sanitizedData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update entry: ${error.message}`);
    }

    // Sanitize body_html again on render
    if (data.body_html) {
      data.body_html = sanitizeForRender(data.body_html);
    }

    return data;
  } catch (error) {
    console.error('Error updating entry:', error);
    throw error;
  }
}

/**
 * Get entry by ID with sanitized body_html for rendering
 */
export async function getEntryById(id: string): Promise<Entry | null> {
  try {
    const { data, error } = await supabase
      .from('entries')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to fetch entry: ${error.message}`);
    }

    // Sanitize body_html for rendering
    if (data?.body_html) {
      data.body_html = sanitizeForRender(data.body_html);
    }

    return data;
  } catch (error) {
    console.error('Error fetching entry:', error);
    throw error;
  }
}